import { invoke } from "~/runtime"
import { useSignal } from "~/sdk/signal"
import { EMAIL_REGEX } from "~/sdk/utils/regex"
import { JSX } from "react"

interface Error {
  field: string
  message?: string
}

interface Result {
  success: boolean
  errors: Error[]
}

export function useSubscribe() {
  const result = useSignal<Result>({ success: false, errors: [] })

  const validateUserInputs = (data: { [key: string]: string }) => {
    const errors: Error[] = []

    if (!data.name?.trim()) {
      errors.push({
        field: 'name',
        message: 'Preencha o nome',
      })
    }

    if (!data.email?.trim()) {
      errors.push({
        field: 'email',
        message: 'Preencha o e-mail',
      })
    }

    if (data.email?.trim() && !EMAIL_REGEX.test(data.email?.trim())) {
      errors.push({
        field: 'email',
        message: 'E-mail inválido',
      })
    }

    if (data.terms !== 'on') {
      errors.push({ field: 'terms' })
    }

    return errors
  }

  const handleSubscribe = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const form = event.currentTarget
    const formData = new FormData(form)
    const data = Object.fromEntries(formData)
    const errors = validateUserInputs(data as { [key: string]: string })

    if (errors?.length) {
      result.value = { success: false, errors }
      return
    }

    try {
      await invoke.site.actions.blackFridayCreateDocument({
        data: { name: data.name.toString(), email: data.email.toString() },
      })

      result.value = { success: true, errors: [] }
    } catch (_error) {
      result.value = {
        success: false,
        errors: [
          {
            field: 'form',
            message: 'Erro inesperado ao se inscrever. Tente novamente.',
          },
        ],
      }
    }
  }

  const isInvalid = (inputName: string) => {
    if (!result.value?.success) {
      return result.value?.errors.some((error) => error.field === inputName)
    }
  }

  const getErrorMessage = (inputName: string) => {
    if (!result.value?.success) {
      return result.value?.errors.find((error) => error.field === inputName)?.message
    }
  }

  const removeErrorOnInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputName = event.currentTarget.name

    result.value = {
      ...result.value,
      errors: result.value?.errors.filter((error) => error.field !== inputName),
    }
  }

  return {
    result,
    handleSubscribe,
    isInvalid,
    getErrorMessage,
    removeErrorOnInput,
  }
}
